import { board, viewBar, type BoardDef } from "./board";
import { el, tipHide } from "./dom";
import { icon } from "./icons";
import {
  hasFilters,
  keepVisible,
  loadState,
  nextSort,
  saveState,
  toggleAll,
  type ListState,
} from "./list-state";
import {
  countLine,
  listParams,
  narrowed,
  pageInfo,
  pageOf,
  PER_PAGE,
  perOf,
  type PageInfo,
  type PerPage,
} from "../../server/rules/list-page";

// A list of records (brief 1A-6, mockup selTable and enhanceTables): a real table with a
// checkbox per row and select-all for the rows on screen, a bulk bar with the count and the
// actions the screen passes in, headers that sort (ascending, descending, off), the header
// filter button that opens a filter row (text contains, or pick a value), stage chips with
// counts, an empty state, and a row click that opens the record. With a board definition it
// also has the List/Board switch. Sort, filters, chip and view are remembered per list and per
// person in this browser.
//
// Brief 1E-32: every list has a search box over its main columns and pages of 20, 50 or 100 with
// Previous, Next and "41–60 of 312"; the page size is kept per list and person. A list given a
// `source` asks the server for each page (search, filters, sort and chip go with it), so a list
// of hundreds opens on its first page fast; a list given `rows` pages the same way on screen.

export interface Column<T> {
  id: string;
  label: string;
  /** The text a row shows here: what sorting and filtering read. */
  text: (row: T) => string;
  /** What the cell shows, when it isn't just the text (bold name, a chip). */
  cell?: (row: T) => Node | string;
  num?: boolean;
  /** Values to pick from in the filter row; without them the filter is "contains". */
  values?: readonly string[];
  /** The search box reads this column (the list's main columns). */
  search?: boolean;
}

/** One page from the server: its rows and the page (total, counts, chips). */
export interface SourcePage<T> {
  rows: T[];
  page: PageInfo;
}

export interface BulkAction<T> {
  label: string;
  icon: string;
  run: (rows: T[]) => void;
}

export interface ListOptions<T> {
  /** Names the list where its choices are remembered, e.g. "staffing/overview". */
  id: string;
  userId: string;
  /** What the rows are, for the count and the table's caption ("placements"). */
  noun: string;
  /** Every row, paged on screen; or `source`, which asks the server for each page. */
  rows?: readonly T[];
  /** Asks the server for one page (q, page, per, sort, f.<col>, p.<col>, chip). */
  source?: (params: URLSearchParams) => Promise<SourcePage<T> | null>;
  /** Narrowing the screen keeps outside the table (a tab, a status pick), sent with each page. */
  extra?: () => Record<string, string>;
  /** The first page, when the screen already asked for it with the list's saved choices. */
  first?: SourcePage<T>;
  rowId: (row: T) => string;
  columns: Column<T>[];
  /** Stage chips over the list ("All 8", "Matched 1"...). */
  chips?: { values: readonly string[]; of: (row: T) => string };
  /** With a board, the List/Board switch shows (list first). */
  board?: BoardDef<T>;
  bulk?: BulkAction<T>[];
  onOpen?: (row: T) => void;
  /** The empty state's words (mockup: "Nothing here."). */
  empty?: string;
  /** The table's name, when it isn't the noun ("Outbox"). */
  caption?: string;
  /** The search box's label ("Search caregivers"). */
  searchLabel?: string;
}

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** The search box over a list: asks a moment after the typing stops, or on Enter. */
export function searchBox(label: string, onSearch: (text: string) => void): HTMLElement {
  const search = el("input", {
    type: "search",
    class: "lsearch",
    placeholder: label,
    "aria-label": label,
    autocomplete: "off",
  });
  let typing: ReturnType<typeof setTimeout> | undefined;
  search.addEventListener("input", () => {
    clearTimeout(typing);
    typing = setTimeout(() => onSearch(search.value), 250);
  });
  search.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    clearTimeout(typing);
    onSearch(search.value);
  });
  return el(
    "div",
    { class: "lsearchrow", role: "search", "data-unsaved": "off" },
    el("span", { class: "lsearchi", "aria-hidden": "true" }, icon("search")),
    search,
  );
}

/** Fills the bar under a list: rows a page (20, 50, 100), "41–60 of 312", Previous and Next. */
export function fillPager(
  pager: HTMLElement,
  info: Pick<PageInfo, "fromRow" | "toRow" | "total" | "page" | "pages">,
  per: PerPage,
  on: { per: (n: PerPage) => Promise<void>; page: (n: number) => Promise<void> },
): void {
  pager.dataset.unsaved = "off";
  const pick = el(
    "select",
    { "aria-label": "Rows a page", class: "pagerper" },
    ...PER_PAGE.map((n) => el("option", { value: String(n), selected: n === per }, String(n))),
  );
  pick.addEventListener("change", async () => {
    await on.per(perOf(pick.value));
    pager.querySelector<HTMLElement>(".pagerper")?.focus();
  });
  const step = (label: string, to: number, can: boolean) => {
    const b = el(
      "button",
      { type: "button", class: "btn sm", disabled: !can, "data-page": label },
      label,
    );
    b.addEventListener("click", async () => {
      await on.page(to);
      pager.querySelector<HTMLElement>(`[data-page="${label}"]`)?.focus();
    });
    return b;
  };
  pager.replaceChildren(
    el("label", { class: "small muted pagerrows" }, "Rows ", pick),
    el("span", { class: "small pagercount", "aria-live": "polite" }, countLine(info)),
    step("Previous", info.page - 1, info.page > 1),
    step("Next", info.page + 1, info.page < info.pages),
  );
}

/** The rows a page this person picked for this list (20 unless changed). */
export const listPer = (listId: string, userId: string): PerPage =>
  loadState(storage(), userId, listId).per;

/** Keeps the rows a page this person picked for this list. */
export function rememberPer(listId: string, userId: string, per: PerPage): void {
  saveState(storage(), userId, listId, { ...loadState(storage(), userId, listId), per });
}

/**
 * The address part for a list's first page with this person's saved choices (sort, filters,
 * chip, rows a page), so a screen can ask for it once and hand it to the list as `first`.
 */
export function firstPageParams(listId: string, userId: string): URLSearchParams {
  const s = loadState(storage(), userId, listId);
  return listParams({ q: "", page: 1, per: s.per, sort: s.sort, filters: s.filters, chip: s.chip });
}

/** A list on screen; `reload()` asks for the page again (after a change, or a tab outside it). */
export type ListElement = HTMLElement & { reload: (fromStart?: boolean) => Promise<void> };

export function list<T>(opts: ListOptions<T>): ListElement {
  const host = el("div", {
    class: "kitlist",
    "data-list": opts.id,
    // Search, filters and rows a page aren't typing to save (brief 1E-23).
    "data-unsaved": "off",
  }) as unknown as ListElement;
  let state: ListState = loadState(storage(), opts.userId, opts.id);
  if (!opts.board) state.view = "list";
  let selected = new Set<string>();
  // The filter row stays open while any filter is set, so what narrows the list is on screen.
  let filtersOpen = hasFilters(state);
  // The search box and the page: for this visit to the list only.
  let q = "";
  let pageNo = 1;
  // What's on screen now: the page's rows and its counts.
  let rows: T[] = [];
  let info: PageInfo = {
    total: 0,
    page: 1,
    per: state.per,
    fromRow: 0,
    toRow: 0,
    pages: 1,
    all: 0,
    chips: [],
    values: {},
  };
  // Every row that matched (the board's columns), when the list pages on screen.
  let boardRows: T[] = [];
  let asked = 0;

  const remember = () => saveState(storage(), opts.userId, opts.id, state);
  const query = () => ({
    q,
    page: pageNo,
    per: state.per,
    sort: state.sort,
    filters: state.filters,
    chip: state.chip,
  });

  /** Reads the page: from the server, or from the rows this list was given. */
  async function load(): Promise<boolean> {
    const n = ++asked;
    if (opts.source) {
      const firstPage = n === 1 && opts.first ? opts.first : null;
      const params = listParams(query());
      for (const [k, v] of Object.entries(opts.extra?.() ?? {})) if (v) params.set(k, v);
      const got = firstPage ?? (await opts.source(params));
      // Only the newest question's answer is drawn; an older one that arrives late is dropped.
      if (n !== asked) return false;
      if (!got) {
        rows = [];
        info = { ...info, total: 0, fromRow: 0, toRow: 0, all: 0, chips: [] };
        return true;
      }
      rows = got.rows;
      info = got.page;
      boardRows = got.rows;
      pageNo = info.page;
      return true;
    }
    const all = opts.rows ?? [];
    const cols = opts.columns.map((c) => ({ ...c, search: c.search ?? true }));
    const p = pageOf(all, cols, query(), opts.chips);
    rows = p.rows;
    info = pageInfo(p);
    pageNo = info.page;
    if (opts.board) boardRows = narrowed(all, cols, query());
    return true;
  }
  const refresh = async () => {
    if (await load()) drawRows();
  };

  // ---- Parts that redraw as filters change, without touching the filter inputs ----------------
  const bar = el("div");
  const chipsHost = el("div");
  const bulkHost = el("div");
  const pager = el("nav", { class: "pager", "aria-label": `Pages of ${opts.noun}` });
  const body = el("tbody");
  const selAll = el("input", { type: "checkbox", "aria-label": "Select all" });
  const head = el("thead");
  const table = el(
    "table",
    { class: "seltable" },
    el("caption", { class: "sr" }, opts.caption ?? opts.noun),
    head,
    body,
  );
  const filterBtn = el(
    "button",
    {
      type: "button",
      class: "tfbtn",
      "aria-label": "Filter rows",
      "data-tip": "Filter by column",
    },
    icon("filter"),
  );
  const tw = el("div", { class: "tw hasf" }, filterBtn, table);
  const views = el("div");

  // The search box over the list's main columns.
  const searchRow = searchBox(opts.searchLabel ?? `Search ${opts.noun}`, (text) => {
    q = text;
    pageNo = 1;
    void refresh();
  });

  filterBtn.addEventListener("click", () => {
    filtersOpen = !filtersOpen;
    if (!filtersOpen && hasFilters(state)) {
      state = { ...state, filters: {} };
      remember();
      pageNo = 1;
      void refresh();
    }
    drawHead();
    filterBtn.setAttribute("aria-expanded", String(filtersOpen));
    if (filtersOpen)
      head.querySelector<HTMLElement>(".filterrow input, .filterrow select")?.focus();
  });
  filterBtn.setAttribute("aria-expanded", String(filtersOpen));

  selAll.addEventListener("change", () => {
    selected = toggleAll(
      selected,
      rows.map((r) => opts.rowId(r)),
    );
    drawRows();
  });

  /** A column's pick list: the screen's own values, else what the page says the column holds. */
  const valuesOf = (c: Column<T>) => c.values ?? info.values[c.id];

  function drawHead() {
    const cells = opts.columns.map((c) => {
      const sorted = state.sort?.col === c.id ? state.sort.dir : null;
      const btn = el("button", { type: "button", class: "sortbtn" }, c.label);
      btn.addEventListener("click", () => {
        state = { ...state, sort: nextSort(state.sort, c.id) };
        remember();
        drawHead();
        void refresh();
        head.querySelector<HTMLElement>(`[data-col="${c.id}"] .sortbtn`)?.focus();
      });
      return el(
        "th",
        {
          class: `sortable${c.num ? " num" : ""}`,
          scope: "col",
          "data-col": c.id,
          "aria-sort": sorted ?? undefined,
          title: "Click to sort",
        },
        btn,
      );
    });
    const out: HTMLElement[] = [
      el("tr", {}, el("th", { class: "cbx", scope: "col" }, selAll), ...cells),
    ];
    if (filtersOpen) {
      out.push(
        el(
          "tr",
          { class: "filterrow" },
          el("th", {}),
          ...opts.columns.map((c) => {
            const f = state.filters[c.id] ?? {};
            const values = valuesOf(c);
            let input: HTMLInputElement | HTMLSelectElement;
            if (values) {
              input = el(
                "select",
                { "aria-label": `Filter ${c.label}` },
                el("option", { value: "" }, "Any"),
                ...values.map((v) => el("option", { value: v, selected: f.pick === v }, v)),
              );
            } else {
              input = el("input", {
                type: "text",
                placeholder: "Filter",
                "aria-label": `Filter ${c.label}`,
                value: f.text ?? "",
              });
            }
            let wait: ReturnType<typeof setTimeout> | undefined;
            input.addEventListener(values ? "change" : "input", () => {
              const filters = Object.fromEntries(
                Object.entries(state.filters).filter(([k]) => k !== c.id),
              );
              if (input.value)
                filters[c.id] = values ? { pick: input.value } : { text: input.value };
              state = { ...state, filters };
              remember();
              pageNo = 1;
              clearTimeout(wait);
              wait = setTimeout(() => void refresh(), values ? 0 : 250);
            });
            return el("th", {}, input);
          }),
        ),
      );
    }
    head.replaceChildren(...out);
  }

  function drawChips() {
    if (!opts.chips || state.view === "board") return chipsHost.replaceChildren();
    const chip = (label: string, value: string | null) => {
      const b = el(
        "button",
        {
          type: "button",
          class: `chipbtn${state.chip === value ? " on" : ""}`,
          "aria-pressed": state.chip === value ? "true" : "false",
        },
        label,
      );
      b.addEventListener("click", async () => {
        state = { ...state, chip: value };
        remember();
        pageNo = 1;
        await refresh();
        const label = value === null ? "All" : value;
        [...chipsHost.querySelectorAll<HTMLElement>(".chipbtn")]
          .find((x) => x.textContent?.startsWith(`${label} `))
          ?.focus();
      });
      return b;
    };
    // The chips in the screen's order; a picked chip with no rows left still shows, so it can be
    // turned off.
    const order = opts.chips.values;
    const chips = info.chips
      .slice()
      .sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
    if (state.chip !== null && !chips.some((c) => c.value === state.chip))
      chips.push({ value: state.chip, count: 0 });
    chipsHost.replaceChildren(
      el(
        "div",
        { class: "stagef", role: "group", "aria-label": "Filter by stage" },
        chip(`All ${info.all}`, null),
        ...chips.map((c) => chip(`${c.value} ${c.count}`, c.value)),
      ),
    );
  }

  function drawBulk() {
    const picked = rows.filter((r) => selected.has(opts.rowId(r)));
    if (!picked.length || state.view === "board") return bulkHost.replaceChildren();
    const clear = el("button", { type: "button", class: "btn sm ghost" }, icon("x"), " Clear");
    clear.addEventListener("click", () => {
      selected = new Set();
      drawRows();
      selAll.focus();
    });
    bulkHost.replaceChildren(
      el(
        "div",
        { class: "bulk", role: "toolbar", "aria-label": "Bulk actions" },
        el("b", { "aria-live": "polite" }, `${picked.length} selected`),
        ...(opts.bulk ?? []).map((a) => {
          const b = el("button", { type: "button", class: "btn sm" }, icon(a.icon), ` ${a.label}`);
          b.addEventListener("click", () => a.run(picked));
          return b;
        }),
        clear,
      ),
    );
  }

  /** Under the list: rows a page, "41–60 of 312", Previous and Next. */
  function drawPager() {
    if (state.view === "board") return pager.replaceChildren();
    fillPager(pager, info, state.per, {
      per: async (n) => {
        state = { ...state, per: n };
        remember();
        pageNo = 1;
        await refresh();
      },
      page: async (n) => {
        pageNo = n;
        await refresh();
      },
    });
  }

  function row(r: T): HTMLElement {
    const id = opts.rowId(r);
    const on = selected.has(id);
    const box = el("input", {
      type: "checkbox",
      checked: on,
      "aria-label": `Select ${opts.columns[0]?.text(r) ?? ""}`.trim(),
    });
    box.addEventListener("change", () => {
      if (box.checked) selected.add(id);
      else selected.delete(id);
      drawRows();
      body.querySelector<HTMLElement>(`tr[data-row="${CSS.escape(id)}"] input`)?.focus();
    });
    const cbx = el("td", { class: "cbx" }, box);
    cbx.addEventListener("click", (e) => {
      e.stopPropagation();
      if (e.target !== box) box.click();
    });
    const tr = el(
      "tr",
      {
        class: `${opts.onOpen ? "go" : ""}${on ? " sel" : ""}`.trim() || undefined,
        "data-row": id,
        tabindex: opts.onOpen ? "0" : undefined,
      },
      cbx,
      ...opts.columns.map((c) =>
        el("td", { class: c.num ? "num" : undefined }, c.cell ? c.cell(r) : c.text(r)),
      ),
    );
    if (opts.onOpen) {
      const open = opts.onOpen;
      tr.addEventListener("click", () => open(r));
      tr.addEventListener("keydown", (e) => {
        if (e.target === tr && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          open(r);
        }
      });
    }
    return tr;
  }

  let drawn = false;
  function drawRows() {
    // A redraw takes away a tip left on a row that's gone; the first draw leaves the page's tips.
    if (drawn) tipHide();
    drawn = true;
    const ids = rows.map((r) => opts.rowId(r));
    selected = keepVisible(selected, ids);
    const count = state.view === "board" ? info.all : info.total;
    bar.replaceChildren(
      viewBar(`${count} ${opts.noun}`, opts.board ? state.view : null, async (v) => {
        state = { ...state, view: v };
        remember();
        await refresh();
        bar.querySelector<HTMLElement>(`.seg2 button[aria-pressed="true"]`)?.focus();
      }),
    );
    drawChips();
    drawBulk();
    drawPager();
    if (state.view === "board" && opts.board) {
      views.replaceChildren(board(opts.board, boardRows, opts.rowId, opts.onOpen));
      return;
    }
    const all = ids.length > 0 && ids.every((id) => selected.has(id));
    selAll.checked = all;
    selAll.indeterminate = !all && selected.size > 0;
    const narrowedNow = q.trim() || hasFilters(state) || state.chip !== null;
    body.replaceChildren(
      ...(rows.length
        ? rows.map(row)
        : [
            el(
              "tr",
              {},
              el(
                "td",
                { colspan: String(opts.columns.length + 1), class: "muted" },
                narrowedNow
                  ? q.trim()
                    ? "Nothing matches this search."
                    : "Nothing matches these filters."
                  : (opts.empty ?? "Nothing here."),
              ),
            ),
          ]),
    );
    views.replaceChildren(tw);
  }

  host.reload = async (fromStart = false) => {
    if (fromStart) pageNo = 1;
    await refresh();
  };
  drawHead();
  host.append(bar, searchRow, chipsHost, bulkHost, views, pager);
  // The first page; the filter row's pick lists come with it.
  void load().then((ok) => {
    if (!ok) return;
    drawHead();
    drawRows();
  });
  return host;
}
